/**
 * LayerStack class for managing multi-layer print simulations
 * Handles layer ordering, visibility, and provides utilities for layer operations
 */

import type {
  PrintLayer,
  LayerStack,
  SubstrateProfile,
  LayerBlendMode,
} from "../../types/substrate";
import type { CMYK } from "../colorEngine";

export class LayerStackManager {
  private layerStack: LayerStack;

  constructor(id: string, name: string, substrate: SubstrateProfile) {
    this.layerStack = {
      id,
      name,
      layers: [],
      substrate,
      metadata: {
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        version: "1.0",
      },
    };
  }

  /**
   * Add a new layer to the stack
   */
  addLayer(layer: Omit<PrintLayer, "id">): PrintLayer {
    const newLayer: PrintLayer = {
      ...layer,
      id: this.generateLayerId(),
    };

    this.layerStack.layers.push(newLayer);
    this.updateModifiedTimestamp();
    return newLayer;
  }

  /**
   * Remove a layer from the stack by ID
   */
  removeLayer(layerId: string): boolean {
    const initialLength = this.layerStack.layers.length;
    this.layerStack.layers = this.layerStack.layers.filter(
      (layer) => layer.id !== layerId,
    );

    if (this.layerStack.layers.length < initialLength) {
      this.updateModifiedTimestamp();
      return true;
    }
    return false;
  }

  /**
   * Update an existing layer
   */
  updateLayer(layerId: string, updates: Partial<PrintLayer>): boolean {
    const layerIndex = this.layerStack.layers.findIndex(
      (layer) => layer.id === layerId,
    );
    if (layerIndex === -1) return false;

    this.layerStack.layers[layerIndex] = {
      ...this.layerStack.layers[layerIndex],
      ...updates,
    };

    this.updateModifiedTimestamp();
    return true;
  }

  /**
   * Get a layer by ID
   */
  getLayer(layerId: string): PrintLayer | undefined {
    return this.layerStack.layers.find((layer) => layer.id === layerId);
  }

  /**
   * Get all layers sorted by zIndex (bottom to top)
   */
  getSortedLayers(): PrintLayer[] {
    return [...this.layerStack.layers].sort((a, b) => a.zIndex - b.zIndex);
  }

  /**
   * Get only visible layers sorted by zIndex
   */
  getVisibleLayers(): PrintLayer[] {
    return this.getSortedLayers().filter((layer) => layer.visible);
  }

  /**
   * Move a layer up in the stack (increase zIndex)
   */
  moveLayerUp(layerId: string): boolean {
    const layer = this.getLayer(layerId);
    if (!layer) return false;

    const layersAbove = this.layerStack.layers.filter(
      (l) => l.zIndex > layer.zIndex,
    );
    if (layersAbove.length === 0) return false;

    const nextLayerAbove = layersAbove.reduce((highest, current) =>
      current.zIndex < highest.zIndex ? current : highest,
    );

    // Swap zIndex values
    const tempZIndex = layer.zIndex;
    layer.zIndex = nextLayerAbove.zIndex;
    nextLayerAbove.zIndex = tempZIndex;

    this.updateModifiedTimestamp();
    return true;
  }

  /**
   * Move a layer down in the stack (decrease zIndex)
   */
  moveLayerDown(layerId: string): boolean {
    const layer = this.getLayer(layerId);
    if (!layer) return false;

    const layersBelow = this.layerStack.layers.filter(
      (l) => l.zIndex < layer.zIndex,
    );
    if (layersBelow.length === 0) return false;

    const nextLayerBelow = layersBelow.reduce((lowest, current) =>
      current.zIndex > lowest.zIndex ? current : lowest,
    );

    // Swap zIndex values
    const tempZIndex = layer.zIndex;
    layer.zIndex = nextLayerBelow.zIndex;
    nextLayerBelow.zIndex = tempZIndex;

    this.updateModifiedTimestamp();
    return true;
  }

  /**
   * Toggle layer visibility
   */
  toggleLayerVisibility(layerId: string): boolean {
    const layer = this.getLayer(layerId);
    if (!layer) return false;

    layer.visible = !layer.visible;
    this.updateModifiedTimestamp();
    return true;
  }

  /**
   * Set layer opacity
   */
  setLayerOpacity(layerId: string, opacity: number): boolean {
    const layer = this.getLayer(layerId);
    if (!layer) return false;

    layer.opacity = Math.max(0, Math.min(1, opacity));
    this.updateModifiedTimestamp();
    return true;
  }

  /**
   * Set layer blend mode
   */
  setLayerBlendMode(layerId: string, blendMode: LayerBlendMode): boolean {
    const layer = this.getLayer(layerId);
    if (!layer) return false;

    layer.blendMode = blendMode;
    this.updateModifiedTimestamp();
    return true;
  }

  /**
   * Duplicate a layer
   */
  duplicateLayer(layerId: string): PrintLayer | null {
    const sourceLayer = this.getLayer(layerId);
    if (!sourceLayer) return null;

    const duplicatedLayer = this.addLayer({
      name: `${sourceLayer.name} (copy)`,
      cmyk: { ...sourceLayer.cmyk },
      opacity: sourceLayer.opacity,
      blendMode: sourceLayer.blendMode,
      visible: sourceLayer.visible,
      zIndex: this.getMaxZIndex() + 1,
      metadata: sourceLayer.metadata ? { ...sourceLayer.metadata } : undefined,
    });

    return duplicatedLayer;
  }

  /**
   * Get the current layer stack
   */
  getLayerStack(): LayerStack {
    return { ...this.layerStack };
  }

  /**
   * Set the substrate for the layer stack
   */
  setSubstrate(substrate: SubstrateProfile): void {
    this.layerStack.substrate = substrate;
    this.updateModifiedTimestamp();
  }

  /**
   * Clear all layers
   */
  clearLayers(): void {
    this.layerStack.layers = [];
    this.updateModifiedTimestamp();
  }

  /**
   * Get layer count
   */
  getLayerCount(): number {
    return this.layerStack.layers.length;
  }

  /**
   * Get visible layer count
   */
  getVisibleLayerCount(): number {
    return this.layerStack.layers.filter((layer) => layer.visible).length;
  }

  /**
   * Validate layer stack integrity
   */
  validate(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check for duplicate zIndex values
    const zIndexValues = this.layerStack.layers.map((layer) => layer.zIndex);
    const uniqueZIndexValues = new Set(zIndexValues);
    if (zIndexValues.length !== uniqueZIndexValues.size) {
      errors.push("Duplicate zIndex values detected");
    }

    // Check for invalid opacity values
    const invalidOpacity = this.layerStack.layers.find(
      (layer) => layer.opacity < 0 || layer.opacity > 1,
    );
    if (invalidOpacity) {
      errors.push(
        `Layer "${invalidOpacity.name}" has invalid opacity: ${invalidOpacity.opacity}`,
      );
    }

    // Check for invalid CMYK values
    const invalidCMYK = this.layerStack.layers.find(
      (layer) =>
        layer.cmyk.c < 0 ||
        layer.cmyk.c > 100 ||
        layer.cmyk.m < 0 ||
        layer.cmyk.m > 100 ||
        layer.cmyk.y < 0 ||
        layer.cmyk.y > 100 ||
        layer.cmyk.k < 0 ||
        layer.cmyk.k > 100,
    );
    if (invalidCMYK) {
      errors.push(`Layer "${invalidCMYK.name}" has invalid CMYK values`);
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Export layer stack as JSON
   */
  export(): string {
    return JSON.stringify(this.layerStack, null, 2);
  }

  /**
   * Import layer stack from JSON
   */
  static import(jsonData: string): LayerStackManager | null {
    try {
      const layerStack: LayerStack = JSON.parse(jsonData);

      // Basic validation
      if (
        !layerStack.id ||
        !layerStack.name ||
        !layerStack.substrate ||
        !Array.isArray(layerStack.layers)
      ) {
        throw new Error("Invalid layer stack format");
      }

      const manager = new LayerStackManager(
        layerStack.id,
        layerStack.name,
        layerStack.substrate,
      );
      manager.layerStack = layerStack;

      return manager;
    } catch (error) {
      console.error("Failed to import layer stack:", error);
      return null;
    }
  }

  private generateLayerId(): string {
    return `layer_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getMaxZIndex(): number {
    if (this.layerStack.layers.length === 0) return 0;
    return Math.max(...this.layerStack.layers.map((layer) => layer.zIndex));
  }

  private updateModifiedTimestamp(): void {
    if (this.layerStack.metadata) {
      this.layerStack.metadata.modified = new Date().toISOString();
    }
  }
}

/**
 * Utility functions for layer operations
 */
export class LayerUtils {
  /**
   * Apply subtractive color blending between two CMYK values
   */
  static blendCMYK(
    bottom: CMYK,
    top: CMYK,
    blendMode: LayerBlendMode,
    opacity: number,
  ): CMYK {
    const topAdjusted = {
      c: top.c * opacity,
      m: top.m * opacity,
      y: top.y * opacity,
      k: top.k * opacity,
    };

    switch (blendMode) {
      case "multiply":
        // Proper subtractive blending: ink accumulates but with diminishing returns
        return {
          c: Math.min(100, bottom.c + topAdjusted.c * (1 - bottom.c / 100)),
          m: Math.min(100, bottom.m + topAdjusted.m * (1 - bottom.m / 100)),
          y: Math.min(100, bottom.y + topAdjusted.y * (1 - bottom.y / 100)),
          k: Math.min(100, bottom.k + topAdjusted.k * (1 - bottom.k / 100)),
        };

      case "screen":
        // Screen blending for CMYK (lightens - inverse of multiply)
        return {
          c: Math.max(0, bottom.c - topAdjusted.c * (bottom.c / 100)),
          m: Math.max(0, bottom.m - topAdjusted.m * (bottom.m / 100)),
          y: Math.max(0, bottom.y - topAdjusted.y * (bottom.y / 100)),
          k: Math.max(0, bottom.k - topAdjusted.k * (bottom.k / 100)),
        };

      case "normal":
      case "overprint":
        // Direct ink addition (simple overlay)
        return {
          c: Math.min(100, bottom.c + topAdjusted.c),
          m: Math.min(100, bottom.m + topAdjusted.m),
          y: Math.min(100, bottom.y + topAdjusted.y),
          k: Math.min(100, bottom.k + topAdjusted.k),
        };

      case "overlay":
        // Overlay: multiply for dark areas, screen for light areas
        return {
          c:
            bottom.c < 50
              ? Math.min(100, bottom.c + topAdjusted.c * (1 - bottom.c / 100))
              : Math.max(0, bottom.c - topAdjusted.c * (bottom.c / 100)),
          m:
            bottom.m < 50
              ? Math.min(100, bottom.m + topAdjusted.m * (1 - bottom.m / 100))
              : Math.max(0, bottom.m - topAdjusted.m * (bottom.m / 100)),
          y:
            bottom.y < 50
              ? Math.min(100, bottom.y + topAdjusted.y * (1 - bottom.y / 100))
              : Math.max(0, bottom.y - topAdjusted.y * (bottom.y / 100)),
          k:
            bottom.k < 50
              ? Math.min(100, bottom.k + topAdjusted.k * (1 - bottom.k / 100))
              : Math.max(0, bottom.k - topAdjusted.k * (bottom.k / 100)),
        };

      case "hard-light":
        // Hard light: inverse of overlay
        return {
          c:
            topAdjusted.c < 50
              ? Math.min(100, bottom.c + topAdjusted.c * (1 - bottom.c / 100))
              : Math.max(0, bottom.c - topAdjusted.c * (bottom.c / 100)),
          m:
            topAdjusted.m < 50
              ? Math.min(100, bottom.m + topAdjusted.m * (1 - bottom.m / 100))
              : Math.max(0, bottom.m - topAdjusted.m * (bottom.m / 100)),
          y:
            topAdjusted.y < 50
              ? Math.min(100, bottom.y + topAdjusted.y * (1 - bottom.y / 100))
              : Math.max(0, bottom.y - topAdjusted.y * (bottom.y / 100)),
          k:
            topAdjusted.k < 50
              ? Math.min(100, bottom.k + topAdjusted.k * (1 - bottom.k / 100))
              : Math.max(0, bottom.k - topAdjusted.k * (bottom.k / 100)),
        };

      case "soft-light":
        // Soft light: gentler version of overlay
        return {
          c: Math.min(
            100,
            bottom.c + topAdjusted.c * (1 - bottom.c / 100) * 0.5,
          ),
          m: Math.min(
            100,
            bottom.m + topAdjusted.m * (1 - bottom.m / 100) * 0.5,
          ),
          y: Math.min(
            100,
            bottom.y + topAdjusted.y * (1 - bottom.y / 100) * 0.5,
          ),
          k: Math.min(
            100,
            bottom.k + topAdjusted.k * (1 - bottom.k / 100) * 0.5,
          ),
        };

      case "knockout":
        // Knockout: remove ink below where top layer has ink
        return {
          c: topAdjusted.c > 0 ? topAdjusted.c : bottom.c,
          m: topAdjusted.m > 0 ? topAdjusted.m : bottom.m,
          y: topAdjusted.y > 0 ? topAdjusted.y : bottom.y,
          k: topAdjusted.k > 0 ? topAdjusted.k : bottom.k,
        };

      default:
        // Default to multiply for unknown blend modes
        return LayerUtils.blendCMYK(bottom, top, "multiply", opacity);
    }
  }

  /**
   * Calculate Total Area Coverage for a CMYK value
   * Standard TAC calculation: sum of all ink percentages
   */
  static calculateTAC(cmyk: CMYK): number {
    return cmyk.c + cmyk.m + cmyk.y + cmyk.k;
  }

  /**
   * Check if a layer exceeds ink limits
   */
  static checkLayerLimits(
    layer: PrintLayer,
    substrate: SubstrateProfile,
  ): {
    exceeded: boolean;
    tac: number;
    individualLimits: {
      cyan: { exceeded: boolean; percentage: number; limit: number };
      magenta: { exceeded: boolean; percentage: number; limit: number };
      yellow: { exceeded: boolean; percentage: number; limit: number };
      black: { exceeded: boolean; percentage: number; limit: number };
    };
  } {
    const tac = LayerUtils.calculateTAC(layer.cmyk);

    return {
      exceeded: tac > substrate.tacLimit,
      tac,
      individualLimits: {
        cyan: {
          exceeded: layer.cmyk.c > substrate.inkLimits.cyan,
          percentage: layer.cmyk.c,
          limit: substrate.inkLimits.cyan,
        },
        magenta: {
          exceeded: layer.cmyk.m > substrate.inkLimits.magenta,
          percentage: layer.cmyk.m,
          limit: substrate.inkLimits.magenta,
        },
        yellow: {
          exceeded: layer.cmyk.y > substrate.inkLimits.yellow,
          percentage: layer.cmyk.y,
          limit: substrate.inkLimits.yellow,
        },
        black: {
          exceeded: layer.cmyk.k > substrate.inkLimits.black,
          percentage: layer.cmyk.k,
          limit: substrate.inkLimits.black,
        },
      },
    };
  }
}
